import React from 'react';
import {
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormLabel,
  Input,
  VStack,
  HStack,
  FormHelperText,
  FormErrorMessage,
  Tooltip,
  Alert,
  AlertIcon,
  Text,
  Link,
  Box,
} from '@chakra-ui/react';
import { InfoIcon } from '@chakra-ui/icons';
import * as _ from 'lodash';

import { UniverseType } from '../../lib/pricing-tool-api';
import { formatDecimalAsPercentage } from '../../lib/numberUtils';
import Choice from '../Choice';
import AbsoluteIntInput from '../AbsNumberInput';
import PercentageInput from '../PercentageInput';

export const UNIVERSE_TYPE_OPTIONS = [
  {
    choiceText: 'Registered voters',
    value: UniverseType.RegisteredVoters,
  },
  {
    choiceText: 'Even year general',
    value: UniverseType.EvenYearGeneral,
    suggestedFilterPercentage: 1.0,
  },
  {
    choiceText: 'Odd year general',
    value: UniverseType.OddYearGeneral,
    suggestedFilterPercentage: 0.8,
  },
  {
    choiceText: 'Dem primary',
    value: UniverseType.DemPrimary,
    suggestedFilterPercentage: 0.6,
  },
  {
    choiceText: 'Local/special election',
    value: UniverseType.LocalSpecialElection,
    suggestedFilterPercentage: 0.6,
    infoText:
      'If it is a general election but the election is local only, select local/special election',
  },
  {
    choiceText: 'All adults',
    value: UniverseType.AllAdults,
  },
  {
    choiceText: 'Something else',
    value: UniverseType.OtherNonStandard,
  },
];

// const EXAMPLE_FILTERS_LINK =
//   'https://docs.google.com/document/d/1F0Vdzdg3pDQM8uBunq8LjHrCa1bZ8F3xNlpo8iiIURo/edit?usp=sharing';

const MIN_UNIVERSES = 0;
const MIN_OVERSAMPLES = 0;

function SpecialDemoInput({
  setSpecialDemoFilterPct,
  specialDemoFilterPct,
  suggestedSpecialDemoFilterPct,
}: {
  setSpecialDemoFilterPct: (val: number) => void;
  specialDemoFilterPct: number;
  suggestedSpecialDemoFilterPct: number;
}) {
  const showSuggestedFilterPctAlert =
    suggestedSpecialDemoFilterPct !== specialDemoFilterPct &&
    suggestedSpecialDemoFilterPct !== 1.0;

  return (
    <FormControl
      textAlign="start"
      isInvalid={specialDemoFilterPct < 0 || specialDemoFilterPct > 1}
    >
      <FormLabel>
        Filter percentage{' '}
        <Tooltip
          placement="right"
          label="Percent of all survey takers that will make it past the special demographic filtering (100 means no filtering)"
        >
          <InfoIcon />
        </Tooltip>
      </FormLabel>
      <PercentageInput
        percentageValue={specialDemoFilterPct}
        setPercentageValue={setSpecialDemoFilterPct}
      />
      {showSuggestedFilterPctAlert && (
        <FormHelperText>
          <Alert status="warning">
            <AlertIcon />
            {`Suggested filter percentage based on Universe Type: ${formatDecimalAsPercentage(
              suggestedSpecialDemoFilterPct,
            )}%`}
          </Alert>
        </FormHelperText>
      )}
      <FormErrorMessage>
        Demo filter percentages should be within 1 and 100!
      </FormErrorMessage>
    </FormControl>
  );
}

function TargetUniverseSection({
  numberOfOversamples,
  numOfAdditionalCrosstabs,
  numOfAdditionalWeightingUniverses,
  numOfAdditionalMonitoringUniverses,
  otherUniverseTypeDescription,
  setNumberOfOversamples,
  setNumOfAdditionalCrosstabs,
  setNumOfAdditionalWeightingUniverses,
  setNumOfAdditionalMonitoringUniverses,
  setOtherUniverseTypeDescription,
  setSpecialDemoFilterPct,
  setUniverseTypes,
  specialDemoFilterPct,
  universeTypes,
  universeCountIsValid,
  universeCountErrorMessage,
  showOtherUniverseTypeDescription,
  setUserInputtedSpecialDemoFilterPct,
  suggestedSpecialDemoFilterPct,
  legacyUniverseTranslated,
}: {
  numberOfOversamples: number;
  numOfAdditionalCrosstabs: number;
  numOfAdditionalWeightingUniverses: number;
  numOfAdditionalMonitoringUniverses: number;
  otherUniverseTypeDescription: string;
  setNumberOfOversamples: (val: number) => void;
  setNumOfAdditionalCrosstabs: (val: number) => void;
  setNumOfAdditionalWeightingUniverses: (val: number) => void;
  setNumOfAdditionalMonitoringUniverses: (val: number) => void;
  setOtherUniverseTypeDescription: (val: string) => void;
  setSpecialDemoFilterPct: (val: number) => void;
  setUniverseTypes: (val: UniverseType[]) => void;
  specialDemoFilterPct: number;
  universeTypes: UniverseType[];
  universeCountIsValid: boolean;
  universeCountErrorMessage: string;
  showOtherUniverseTypeDescription: boolean;
  setUserInputtedSpecialDemoFilterPct: (val: boolean) => void;
  suggestedSpecialDemoFilterPct: number;
  legacyUniverseTranslated: boolean;
}) {
  const onUserChangeSpecialDemoFilterPct = (val: number) => {
    setUserInputtedSpecialDemoFilterPct(true);
    setSpecialDemoFilterPct(val);
  };

  const totalUniverses = 1+ numOfAdditionalCrosstabs + numOfAdditionalWeightingUniverses + numOfAdditionalMonitoringUniverses;

  const decideIfUniverseChoiceDisabled = (value: string) => {
    if (_.includes(universeTypes, value)) {
      return false;
    }
    if (universeTypes.length < totalUniverses) {
      return false;
    }
    return true;
  };

  // Count how many of the four fields have non-zero values
  const nonZeroFieldsCount = [
    numOfAdditionalCrosstabs,
    numOfAdditionalWeightingUniverses,
    numOfAdditionalMonitoringUniverses,
    numberOfOversamples
  ].filter(val => val > 0).length;

  const showMultipleFieldsWarning = nonZeroFieldsCount > 1;

  return (
    <VStack spacing="2%" align="stretch">
      <Text fontSize="sm" color="gray.600" mb="4" textAlign="left">
        Additional sampling complexity. It is rare that you would have non-zero values for more than one of these 4 text boxes.
      </Text>

      {showMultipleFieldsWarning && (
        <Alert status="warning" mb="4">
          <AlertIcon />
          <Box>
            It is rare, but possible, that you would have non-zero values for more than one of these 4 text boxes. If you aren&apos;t sure if you are doing this properly, please ask in the{' '}
            <Link href="https://savingtheuniverse.slack.com/archives/CBJBCKQSW" color="blue.600" isExternal>
              #pricing channel
            </Link>.
          </Box>
        </Alert>
      )}

      <FormControl>
        <FormLabel>
          Additional crosstabs ($5,000 each){' '}
          <Tooltip
            placement="right"
            label="Ex: you are surveying 4 states and you will deliver a spreadsheet for the combined results AND a separate spreadsheet for each state, input 4 additional crosstabs."
          >
            <InfoIcon />
          </Tooltip>
        </FormLabel>
        <AbsoluteIntInput
          numberVal={numOfAdditionalCrosstabs}
          setNumberVal={setNumOfAdditionalCrosstabs}
          minVal={MIN_UNIVERSES}
        />
      </FormControl>

      <FormControl>
        <FormLabel>
          Additional weighting universes ($2,000 each){' '}
          <Tooltip
            placement="right"
            label="Ex: you are surveying 4 states and you will deliver one single spreadsheet but you will have columns for each state, and you will apply at least some weights at the per-state level."
          >
            <InfoIcon />
          </Tooltip>
        </FormLabel>
        <AbsoluteIntInput
          numberVal={numOfAdditionalWeightingUniverses}
          setNumberVal={setNumOfAdditionalWeightingUniverses}
          minVal={MIN_UNIVERSES}
        />
      </FormControl>

      <FormControl>
        <FormLabel>
          Additional monitoring (non-weighting) universes ($1,000 for the first, $300 for subsequent ones){' '}
          <Tooltip
            placement="right"
            label="Ex: you are surveying 4 states and you will deliver one single spreadsheet. You will not apply weights at the state-level but will get the approximately the right number of responses for each state."
          >
            <InfoIcon />
          </Tooltip>
        </FormLabel>
        <AbsoluteIntInput
          numberVal={numOfAdditionalMonitoringUniverses}
          setNumberVal={setNumOfAdditionalMonitoringUniverses}
          minVal={MIN_UNIVERSES}
        />
      </FormControl>

      <FormControl>
        <FormLabel>
          How many oversamples? ($3,500 each){' '}
          <Tooltip
            placement="right"
            label="Number of oversamples."
          >
            <InfoIcon />
          </Tooltip>
        </FormLabel>
        <AbsoluteIntInput
          numberVal={numberOfOversamples}
          setNumberVal={setNumberOfOversamples}
          minVal={MIN_OVERSAMPLES}
        />
      </FormControl>

      {legacyUniverseTranslated && (
        <Alert status="info" mb="4">
          <AlertIcon />
          <Box>
            This quote originally used the &quot;Primary/local/special election&quot;
            universe type, which has since been split into &quot;Dem primary&quot;
            and &quot;Local/special election.&quot; We&apos;ve auto-selected
            &quot;Local/special election&quot; — please confirm or switch to
            &quot;Dem primary&quot; if that&apos;s a better fit.
          </Box>
        </Alert>
      )}

      <FormControl isRequired isInvalid={!universeCountIsValid}>
        <FormLabel>Universe Type(s)</FormLabel>
        <CheckboxGroup
          value={universeTypes}
          onChange={(vals) =>
            setUniverseTypes(_.map(vals, (val) => val as UniverseType))
          }
        >
          <VStack flexDir="column" align="start">
            {UNIVERSE_TYPE_OPTIONS.map(({ choiceText, value, infoText }: Choice) => (
              <Checkbox
                disabled={decideIfUniverseChoiceDisabled(value)}
                value={value}
                key={`universe-type-${value}`}
              >
                <HStack spacing={2} align="center">
                  <Tooltip
                    placement="right"
                    label={
                      decideIfUniverseChoiceDisabled(value)
                        ? 'Can only choose equal to calculated universe total (additional crosstabs + additional weighting + additional monitoring), add more universes or uncheck others to select this one'
                        : ''
                    }
                  >
                    <Text>{choiceText}</Text>
                  </Tooltip>
                  {infoText && (
                    <Tooltip placement="right" label={infoText}>
                      <InfoIcon boxSize={3} />
                    </Tooltip>
                  )}
                </HStack>
              </Checkbox>
            ))}

            <FormHelperText>
              You can only select as many universe types as the total number of
              additional universes (crosstabs + weighting + monitoring).
            </FormHelperText>
          </VStack>
        </CheckboxGroup>

        {!universeCountIsValid && (
          <FormErrorMessage>{universeCountErrorMessage}</FormErrorMessage>
        )}
      </FormControl>

      {showOtherUniverseTypeDescription && (
        <FormControl isRequired>
          <FormLabel>
            Please describe the other universe type you are weighting to:
          </FormLabel>
          <Input
            value={otherUniverseTypeDescription}
            onChange={(e) => setOtherUniverseTypeDescription(e.target.value)}
          />
        </FormControl>
      )}

      <SpecialDemoInput
        setSpecialDemoFilterPct={onUserChangeSpecialDemoFilterPct}
        specialDemoFilterPct={specialDemoFilterPct}
        suggestedSpecialDemoFilterPct={suggestedSpecialDemoFilterPct}
      />
    </VStack>
  );
}

export default TargetUniverseSection;
